import { useState } from "react";

import { Icon } from "./Icon";
import type { KbStep } from "../services/api";

/**
 * Interactive guided walkthrough — the phase-two self-service centrepiece.
 * Numbered-node steps (the same visual language as the approval timeline) that
 * the reader ticks off, with a progress bar and a "you sorted it yourself"
 * completion state. Tick state is per-device UI only, persisted in localStorage
 * keyed by article id (ephemeral; no backend).
 */
export function GuidedSteps({ steps, articleId }: { steps: KbStep[]; articleId: string }) {
  const storageKey = `kb-steps-${articleId}`;
  const [done, setDone] = useState<boolean[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const saved = raw ? (JSON.parse(raw) as boolean[]) : [];
      return steps.map((_, i) => Boolean(saved[i]));
    } catch {
      return steps.map(() => false);
    }
  });

  const count = done.filter(Boolean).length;
  const pct = steps.length ? Math.round((count / steps.length) * 100) : 0;
  const allDone = count === steps.length && steps.length > 0;

  function toggle(i: number) {
    setDone((prev) => {
      const next = prev.map((v, j) => (j === i ? !v : v));
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* storage unavailable — tick state is best-effort */
      }
      return next;
    });
  }

  return (
    <div className="kb-steps">
      <div className="kb-steps-head">
        <span className="kb-steps-lbl">
          <Icon name="list" size={14} />
          Guided walkthrough
        </span>
        <div className="kb-steps-prog">
          <span className="pct">
            {count} / {steps.length} done
          </span>
          <div className="kb-prog-track">
            <div className="kb-prog-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {steps.map((s, i) => (
        <button
          type="button"
          key={i}
          className={"kb-step" + (done[i] ? " done" : "")}
          onClick={() => toggle(i)}
          aria-pressed={done[i]}
        >
          <span className="kb-step-rail">
            <span className="kb-step-node">{done[i] ? <Icon name="check" size={15} /> : i + 1}</span>
            {i < steps.length - 1 && <span className={"kb-step-line" + (done[i] ? " done" : "")} />}
          </span>
          <span className="kb-step-body">
            <span className="kb-step-title">{s.title}</span>
            <span className="kb-step-detail">{s.detail}</span>
            {s.tip && (
              <span className="kb-step-tip">
                <Icon name="bulb" size={16} />
                <span>{s.tip}</span>
              </span>
            )}
          </span>
          <span className="kb-step-do">{done[i] ? "Done" : "Mark done"}</span>
        </button>
      ))}

      {allDone && (
        <div className="kb-steps-done">
          <Icon name="check" size={22} />
          <div>
            <div className="t">All steps complete</div>
            <div className="s">
              Nicely done &mdash; you sorted it yourself. If it&apos;s still not right, raise a
              General IT Support request and we&apos;ll take it from here.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuidedSteps;
